import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  PromptItem,
  CoachingStyle,
  AppLanguage,
  SourcesOfTruth,
  OpenAIModel,
  COACHING_STYLES,
  APP_LANGUAGES,
  SOURCES_OF_TRUTH,
  OPENAI_MODELS,
  CostTypes,
  SearchedUser,
  OfficeHoursMessage,
  SolutionSessionMessage,
} from '@backrs_ui/grow-ui/src/models/models';

import { incrementCostsThunk, initiateSession } from './coachLubav.thunk';
import { logout } from '../session/logoutThunk';

export type CostsState = Record<CostTypes, number>;

type CoachLubavState = Readonly<{
  loading: boolean;
  sessionId?: number;
  coachingStyle: CoachingStyle;
  language: AppLanguage;
  sourcesOfTruth: SourcesOfTruth;
  openAIModel: OpenAIModel;
  coachingUser?: SearchedUser;
  officeHoursMessages: OfficeHoursMessage[];
  solutionSessionMessages: SolutionSessionMessage[];
  suggestedPrompts: PromptItem[];
  costs: CostsState;
  error?: string;
}>;

export const initialState: CoachLubavState = {
  loading: true,
  sessionId: undefined,
  coachingStyle: COACHING_STYLES.CONVERSATIONAL,
  language: APP_LANGUAGES.ENGLISH,
  sourcesOfTruth: SOURCES_OF_TRUTH.ALL,
  openAIModel: OPENAI_MODELS.SMARTEST,
  officeHoursMessages: [],
  solutionSessionMessages: [],
  suggestedPrompts: [],
  costs: {
    total_cost: 0.0,
    prompt_tokens: 0.0,
    completion_tokens: 0.0,
  },
};

export const coachLubavSlice = createSlice({
  name: 'coachLubav',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCoachingStyle: (state, action: PayloadAction<CoachingStyle>) => {
      state.coachingStyle = action.payload;
    },
    setAppLanguage: (state, action: PayloadAction<AppLanguage>) => {
      state.language = action.payload;
    },
    setSourcesOfTruth: (state, action: PayloadAction<SourcesOfTruth>) => {
      state.sourcesOfTruth = action.payload;
    },
    setOpenAIModel: (state, action: PayloadAction<OpenAIModel>) => {
      state.openAIModel = action.payload;
    },
    setCoachingUser: (state, action: PayloadAction<SearchedUser | undefined>) => {
      state.coachingUser = action.payload;
    },
    addOfficeHoursMessage: (state, action: PayloadAction<OfficeHoursMessage>) => {
      state.officeHoursMessages = [...state.officeHoursMessages, action.payload];
    },
    setOfficeHoursMessages: (state, action: PayloadAction<OfficeHoursMessage[]>) => {
      state.officeHoursMessages = action.payload;
    },
    setSolutionSessionMessages: (state, action: PayloadAction<SolutionSessionMessage[]>) => {
      state.solutionSessionMessages = action.payload;
    },
    addSolutionSessionMessage: (state, action: PayloadAction<SolutionSessionMessage>) => {
      state.solutionSessionMessages = [...state.solutionSessionMessages, action.payload];
    },
    resetLubavMessages: (state) => {
      state.officeHoursMessages = [];
      state.solutionSessionMessages = [];
      state.coachingUser = undefined;
    },
    deleteLastMessage: (state) => {
      if (state.officeHoursMessages.length > 0) {
        state.officeHoursMessages = state.officeHoursMessages.slice(0, -1);
      }
    },
    updateLastMessage: (state, action: PayloadAction<Partial<OfficeHoursMessage>>) => {
      if (state.officeHoursMessages.length > 0) {
        const lastIndex = state.officeHoursMessages.length - 1;

        state.officeHoursMessages[lastIndex] = {
          ...state.officeHoursMessages[lastIndex],
          ...action.payload,
        };
      }
    },
    setSuggestedPrompts: (state, action: PayloadAction<PromptItem[]>) => {
      state.suggestedPrompts = action.payload;
    },
    resetSuggestedPrompts: (state) => {
      state.suggestedPrompts = [];
    },
    resetCurrentSession: (state) => {
      state.sessionId = undefined;
    },
    setSessionId: (state, action: PayloadAction<number>) => {
      state.sessionId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(incrementCostsThunk.fulfilled, (state, action) => {
        state.costs = action.payload;
      })
      // LOGOUT
      .addCase(logout, (state) => {
        state.sessionId = undefined;
        state.coachingStyle = initialState.coachingStyle;
        state.language = initialState.language;
        state.sourcesOfTruth = initialState.sourcesOfTruth;
        state.openAIModel = initialState.openAIModel;
        state.loading = false;
        state.error = undefined;
      })
      .addCase(initiateSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(initiateSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(
        initiateSession.fulfilled,
        (state, action: ReturnType<typeof initiateSession.fulfilled>) => {
          if (action.payload) {
            if (action.meta.arg.activeSession === action.payload.activeSession) {
              state.sessionId = action.payload.sessionId;
            }
          }

          state.loading = false;
        },
      );
  },
});

export const {
  setCoachingStyle,
  setAppLanguage,
  setSourcesOfTruth,
  setOpenAIModel,
  setIsLoading,
  setCoachingUser,
  setOfficeHoursMessages,
  setSolutionSessionMessages,
  addOfficeHoursMessage,
  addSolutionSessionMessage,
  resetLubavMessages,
  deleteLastMessage,
  updateLastMessage,
  setSuggestedPrompts,
  resetSuggestedPrompts,
  resetCurrentSession,
  setSessionId,
} = coachLubavSlice.actions;

// Reducer
export const coachLubavReducer = coachLubavSlice.reducer;

// Selectors
// only add selectors when you need them to do some sort of logic. Otherwise, just use the state directly
